import { useState, useEffect } from 'react';

const API = 'https://ts-team-shopify.anonnaabir.workers.dev';

interface Member { id: string; name: string; designation: string; description: string; image_url: string; }
interface Props { showcaseId: string; onBack: () => void; onSave: () => void; }

const D: Record<string, any> = {
  view:'grid', layout:'Card', columns:3, columnGap:20, containerWidth:1200,
  cardBg:'#ffffff', cardBorderRadius:8, cardBorder:'1px solid #e0e0e0', cardShadow:'0 2px 8px rgba(0,0,0,0.08)', cardPadding:20,
  imageHeight:250, imageBorderRadius:0,
  nameColor:'#1a1a1a', nameFontSize:18, nameFontWeight:'600',
  designationColor:'#666666', designationFontSize:14,
  descriptionColor:'#444444', descriptionFontSize:14,
  showDesignation:true, showDescription:true,
  hoverAnimation:'none', backgroundColor:'', padding:40,
};

export default function ShowcaseEditor({ showcaseId, onBack, onSave }: Props) {
  const [members, setMembers] = useState<Member[]>([]);
  const [sel, setSel] = useState<string[]>([]);
  const [s, setS] = useState<Record<string,any>>({...D});
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [vp, setVp] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      Promise.all([
        fetch(`${API}/api/team-showcases`).then(r => r.json()),
        fetch(`${API}/api/team-members`).then(r => r.json()),
      ]).then(([sd, md]) => {
        if (md.success) setMembers(md.data || []);
        if (sd.success) {
          const sc = (sd.data || []).find((x: any) => x.id === showcaseId);
          if (sc) {
            setName(sc.name || '');
            try { setS({ ...D, ...(JSON.parse(sc.settings || '{}')) }); } catch {}
            try { setSel(JSON.parse(sc.team_member_ids || '[]')); } catch {}
          }
        }
      }).catch((e) => {
        setError('Fetch error: ' + e.message);
        console.error(e);
      }).finally(() => setLoading(false));
    } catch (e: any) {
      setError('Init error: ' + e.message);
      setLoading(false);
    }
  }, [showcaseId]);

  const u = (k: string, v: any) => setS(p => ({ ...p, [k]: v }));
  const tg = (id: string) => setSel(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const picked = members.filter(m => sel.includes(m.id));

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/team-showcases/${showcaseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, settings: s, teamMemberIds: sel }),
      });
      onSave();
    } catch (e: any) {
      setError('Save error: ' + e.message);
    }
    setSaving(false);
  };

  if (error) return <div style={{ padding: 40, color: 'red' }}>Error: {error}</div>;
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading editor for {showcaseId}...</div>;

  const tabs = ['Content', 'Style', 'Typography', 'Members'];
  const vpW = ['100%', '768px', '375px'][vp];

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f4f6f8' }}>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, background: '#ff0', padding: '4px 8px', fontSize: 11, zIndex: 9999 }}>
        EDITOR LOADED | showcaseId: {showcaseId} | members: {members.length} | selected: {sel.length} | tab: {tab}
      </div>
      {/* Sidebar */}
      <div style={{ width: 320, borderRight: '1px solid #ddd', display: 'flex', flexDirection: 'column', background: '#fff', flexShrink: 0 }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={onBack} style={{ padding: '4px 12px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>← Back</button>
          <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{name || 'Showcase'}</span>
          <button onClick={save} disabled={saving} style={{ padding: '4px 12px', border: 'none', borderRadius: 6, background: '#5c6ac4', color: '#fff', cursor: 'pointer', fontSize: 13 }}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid #ddd' }}>
          {tabs.map((t, i) => (
            <button key={i} onClick={() => setTab(i)} style={{
              flex: 1, padding: '8px 0', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12,
              fontWeight: tab === i ? 600 : 400, borderBottom: tab === i ? '2px solid #5c6ac4' : '2px solid transparent',
              color: tab === i ? '#5c6ac4' : '#666',
            }}>{t}</button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {tab === 0 && <ContentTab s={s} u={u} name={name} setName={setName} />}
          {tab === 1 && <StyleTab s={s} u={u} />}
          {tab === 2 && <TypeTab s={s} u={u} />}
          {tab === 3 && <MembersTab members={members} sel={sel} tg={tg} />}
        </div>
      </div>

      {/* Preview */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: 8, background: '#fff' }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Preview</span>
          <div style={{ flex: 1 }} />
          {['Desktop', 'Tablet', 'Mobile'].map((v, i) => (
            <button key={i} onClick={() => setVp(i)} style={{
              padding: '4px 10px', border: vp === i ? '2px solid #5c6ac4' : '1px solid #ddd',
              borderRadius: 6, background: vp === i ? '#f0f1ff' : '#fff', cursor: 'pointer', fontSize: 12,
            }}>{v}</button>
          ))}
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: vpW, maxWidth: '100%', background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', padding: 16 }}>
            {picked.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Select members to preview</div> : <Preview members={picked} s={s} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- SUB COMPONENTS ---

function ContentTab({ s, u, name, setName }: any) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <Field label="Name" value={name} onChange={setName} />
    <SelectField label="View" value={s.view} options={[{ l: 'Grid', v: 'grid' }, { l: 'Carousel', v: 'carousel' }, { l: 'Flex', v: 'flex' }, { l: 'Marquee', v: 'marquee' }, { l: 'Table', v: 'table' }]} onChange={(v: string) => u('view', v)} />
    {s.view === 'grid' && <SelectField label="Layout" value={s.layout} options={[{ l: 'Card', v: 'Card' }, { l: 'Horizontal Card', v: 'HorizontalCard' }, { l: 'Overlay Card', v: 'OverlayCard' }, { l: 'Tiles', v: 'Tiles' }, { l: 'Spotlight', v: 'Spotlight' }, { l: 'Corner Frame', v: 'CornerFrame' }, { l: 'Flip Box', v: 'Flipbox' }, { l: 'Aura Live', v: 'AuraLive' }]} onChange={(v: string) => u('layout', v)} />}
    {s.view === 'flex' && <SelectField label="Layout" value={s.layout} options={[{ l: 'Split Left', v: 'split-left' }, { l: 'Split Right', v: 'split-right' }]} onChange={(v: string) => u('layout', v)} />}
    {s.view === 'grid' && <><Field label="Columns" value={String(s.columns)} onChange={(v: string) => u('columns', parseInt(v) || 3)} type="number" /><Field label="Gap (px)" value={String(s.columnGap)} onChange={(v: string) => u('columnGap', parseInt(v) || 20)} type="number" /></>}
    <Field label="Width (px)" value={String(s.containerWidth)} onChange={(v: string) => u('containerWidth', parseInt(v) || 1200)} type="number" />
    <SelectField label="Hover" value={s.hoverAnimation} options={[{ l: 'None', v: 'none' }, { l: 'Float 3D', v: 'float3d' }, { l: 'Tilt', v: 'tilt' }, { l: 'Shadow Pop', v: 'shadowPop' }]} onChange={(v: string) => u('hoverAnimation', v)} />
  </div>;
}

function StyleTab({ s, u }: any) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <h4 style={{ margin: 0 }}>Card</h4>
    <Field label="Background" value={s.cardBg} onChange={(v: string) => u('cardBg', v)} />
    <Field label="Border Radius" value={String(s.cardBorderRadius)} onChange={(v: string) => u('cardBorderRadius', parseInt(v) || 0)} type="number" />
    <Field label="Border" value={s.cardBorder} onChange={(v: string) => u('cardBorder', v)} />
    <Field label="Shadow" value={s.cardShadow} onChange={(v: string) => u('cardShadow', v)} />
    <Field label="Padding" value={String(s.cardPadding)} onChange={(v: string) => u('cardPadding', parseInt(v) || 20)} type="number" />
    <hr />
    <h4 style={{ margin: 0 }}>Image</h4>
    <Field label="Height" value={String(s.imageHeight)} onChange={(v: string) => u('imageHeight', parseInt(v) || 250)} type="number" />
    <hr />
    <h4 style={{ margin: 0 }}>Section</h4>
    <Field label="Background" value={s.backgroundColor} onChange={(v: string) => u('backgroundColor', v)} />
    <Field label="Padding" value={String(s.padding)} onChange={(v: string) => u('padding', parseInt(v) || 40)} type="number" />
  </div>;
}

function TypeTab({ s, u }: any) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <h4 style={{ margin: 0 }}>Name</h4>
    <Field label="Color" value={s.nameColor} onChange={(v: string) => u('nameColor', v)} />
    <Field label="Size" value={String(s.nameFontSize)} onChange={(v: string) => u('nameFontSize', parseInt(v) || 18)} type="number" />
    <SelectField label="Weight" value={s.nameFontWeight} options={[{ l: 'Regular', v: '400' }, { l: 'Medium', v: '500' }, { l: 'Semi Bold', v: '600' }, { l: 'Bold', v: '700' }]} onChange={(v: string) => u('nameFontWeight', v)} />
    <hr />
    <h4 style={{ margin: 0 }}>Designation</h4>
    <Field label="Color" value={s.designationColor} onChange={(v: string) => u('designationColor', v)} />
    <Field label="Size" value={String(s.designationFontSize)} onChange={(v: string) => u('designationFontSize', parseInt(v) || 14)} type="number" />
    <hr />
    <h4 style={{ margin: 0 }}>Description</h4>
    <Field label="Color" value={s.descriptionColor} onChange={(v: string) => u('descriptionColor', v)} />
    <Field label="Size" value={String(s.descriptionFontSize)} onChange={(v: string) => u('descriptionFontSize', parseInt(v) || 14)} type="number" />
    <hr />
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><input type="checkbox" checked={s.showDesignation} onChange={e => u('showDesignation', e.target.checked)} /> Show Designation</label>
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><input type="checkbox" checked={s.showDescription} onChange={e => u('showDescription', e.target.checked)} /> Show Description</label>
  </div>;
}

function MembersTab({ members, sel, tg }: any) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <h4 style={{ margin: 0 }}>{sel.length} selected</h4>
    {members.map((m: Member) => (
      <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
        <input type="checkbox" checked={sel.includes(m.id)} onChange={() => tg(m.id)} />
        {m.image_url && <img src={m.image_url} width="28" height="28" style={{ borderRadius: '50%', objectFit: 'cover' }} />}
        <div><div style={{ fontWeight: 500, fontSize: 13 }}>{m.name}</div>{m.designation && <div style={{ fontSize: 11, color: '#888' }}>{m.designation}</div>}</div>
      </label>
    ))}
  </div>;
}

// --- FORM HELPERS ---

function Field({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return <div><label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>{label}</label><input type={type} value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%', padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} /></div>;
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: { l: string; v: string }[]; onChange: (v: string) => void }) {
  return <div><label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>{label}</label><select value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%', padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>{options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}</select></div>;
}

// --- PREVIEW ---

function Preview({ members, s }: { members: Member[]; s: Record<string, any> }) {
  const view = s.view || 'grid', layout = s.layout || 'Card', cols = s.columns || 3, gap = s.columnGap || 20;
  if (view === 'table') return <table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr>{['', 'Name', 'Role', 'About'].map(h => <th key={h} style={{ padding: 8, textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontSize: 12 }}>{h}</th>)}</tr></thead><tbody>{members.map((m, i) => <tr key={i}><td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{m.image_url && <img src={m.image_url} width="36" height="36" style={{ borderRadius: '50%', objectFit: 'cover' }} />}</td><td style={{ padding: 8, borderBottom: '1px solid #eee', fontWeight: 600 }}>{m.name}</td><td style={{ padding: 8, borderBottom: '1px solid #eee', color: '#666' }}>{m.designation}</td><td style={{ padding: 8, borderBottom: '1px solid #eee', color: '#444' }}>{m.description}</td></tr>)}</tbody></table>;
  if (view === 'flex') return <div style={{ display: 'flex', flexDirection: 'column', gap }}>{members.map((m, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap, flexDirection: layout === 'split-right' ? 'row-reverse' : 'row' }}>{m.image_url && <div style={{ flex: '0 0 40%' }}><img src={m.image_url} width="100%" height={s.imageHeight || 250} style={{ objectFit: 'cover', borderRadius: s.cardBorderRadius || 8 }} /></div>}<div style={{ flex: 1, padding: s.cardPadding || 20 }}><div style={{ fontSize: s.nameFontSize || 18, fontWeight: s.nameFontWeight || '600', color: s.nameColor || '#1a1a1a', marginBottom: 4 }}>{m.name}</div>{s.showDesignation !== false && m.designation && <div style={{ fontSize: s.designationFontSize || 14, color: s.designationColor || '#666', marginBottom: 6 }}>{m.designation}</div>}{s.showDescription !== false && m.description && <div style={{ fontSize: s.descriptionFontSize || 14, color: s.descriptionColor || '#444', lineHeight: 1.5 }}>{m.description}</div>}</div></div>)}</div>;
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>{members.map((m, i) => <Card0 key={i} m={m} s={s} l={layout} />)}</div>;
}

function Card0({ m, s, l }: { m: Member; s: Record<string, any>; l: string }) {
  const nC = s.nameColor || '#1a1a1a', nS = s.nameFontSize || 18, nW = s.nameFontWeight || '600';
  const dC = s.designationColor || '#666', dS = s.designationFontSize || 14;
  const dcC = s.descriptionColor || '#444', dcS = s.descriptionFontSize || 14;
  const bg = s.cardBg || '#fff', br = s.cardBorderRadius || 8, bd = s.cardBorder || '1px solid #e0e0e0';
  const sh = s.cardShadow || '0 2px 8px rgba(0,0,0,0.08)', pd = s.cardPadding || 20, ih = s.imageHeight || 250;
  const ht = s.hoverAnimation === 'float3d' ? 'translateY(-4px)' : s.hoverAnimation === 'tilt' ? 'perspective(500px) rotateY(3deg)' : 'none';
  const st: React.CSSProperties = { background: bg, borderRadius: br, border: bd, boxShadow: sh, overflow: 'hidden', transition: 'transform .3s,box-shadow .3s', transform: ht };
  const img = m.image_url ? <div style={{ overflow: 'hidden', height: ih }}><img src={m.image_url} width="100%" height={ih} style={{ objectFit: 'cover' }} /></div> : null;
  const info = <div style={{ padding: pd }}>
    <div style={{ fontSize: nS, fontWeight: nW as any, color: nC, marginBottom: 4 }}>{m.name}</div>
    {s.showDesignation !== false && m.designation && <div style={{ fontSize: dS, color: dC, marginBottom: 6 }}>{m.designation}</div>}
    {s.showDescription !== false && m.description && <div style={{ fontSize: dcS, color: dcC, lineHeight: 1.5 }}>{m.description}</div>}
  </div>;
  if (l === 'HorizontalCard') return <div style={{ ...st, display: 'flex' }}>{m.image_url && <div style={{ flex: '0 0 140px', overflow: 'hidden' }}><img src={m.image_url} width="140" height={ih} style={{ objectFit: 'cover' }} /></div>}<div style={{ padding: pd, flex: 1 }}><div style={{ fontSize: nS, fontWeight: nW as any, color: nC }}>{m.name}</div>{s.showDesignation !== false && m.designation && <div style={{ fontSize: dS, color: dC }}>{m.designation}</div>}</div></div>;
  if (l === 'OverlayCard') return <div style={{ ...st, position: 'relative', minHeight: ih + 60 }}>{m.image_url && <img src={m.image_url} width="100%" height={ih} style={{ objectFit: 'cover', position: 'absolute', inset: 0 }} />}<div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent,rgba(0,0,0,0.8))', padding: pd, color: '#fff' }}><div style={{ fontSize: nS, fontWeight: nW as any }}>{m.name}</div></div></div>;
  if (l === 'Tiles') return <div style={{ ...st, textAlign: 'center' }}>{img}{info}</div>;
  if (l === 'Spotlight') return <div style={{ ...st, display: 'flex', alignItems: 'center', gap: pd, padding: pd }}>{m.image_url && <div style={{ flex: '0 0 56px', height: 56, borderRadius: '50%', overflow: 'hidden' }}><img src={m.image_url} width="56" height="56" style={{ objectFit: 'cover' }} /></div>}<div><div style={{ fontSize: nS, fontWeight: nW as any, color: nC }}>{m.name}</div>{s.showDesignation !== false && m.designation && <div style={{ fontSize: dS, color: dC }}>{m.designation}</div>}</div></div>;
  if (l === 'CornerFrame') return <div style={{ ...st, border: `2px solid ${nC}` }}>{img}{info}</div>;
  if (l === 'Flipbox') return <div style={{ ...st, background: nC, color: '#fff', padding: pd, textAlign: 'center' }}><div style={{ fontSize: nS, fontWeight: nW as any }}>{m.name}</div>{s.showDesignation !== false && m.designation && <div style={{ fontSize: dS, opacity: 0.9 }}>{m.designation}</div>}{s.showDescription !== false && m.description && <div style={{ fontSize: dcS, opacity: 0.8, lineHeight: 1.5, marginTop: 8 }}>{m.description}</div>}</div>;
  if (l === 'AuraLive') return <div style={st}>{img}<div style={{ padding: pd, marginTop: -20, position: 'relative' }}><div style={{ fontSize: nS, fontWeight: nW as any, color: nC }}>{m.name}</div>{s.showDesignation !== false && m.designation && <div style={{ fontSize: dS, color: dC }}>{m.designation}</div>}</div></div>;
  return <div style={st}>{img}{info}</div>;
}
